import type { MatchSnapshot } from "@/lib/matches";
import { getStoredLang, t as translate } from "@/lib/i18n";

/**
 * Match Clock Engine — derives phase, stoppage, remaining time and a critical
 * flag from the (minute, status) tuple already produced by the simulation.
 * Pure functions only, no side-effects, safe for SSR.
 */

export type MatchPhase =
  | "pre_match"
  | "first_half"
  | "halftime"
  | "second_half"
  | "extra_time"
  | "penalties"
  | "full_time"
  | "suspended";

export interface MatchClockState {
  /** Logical minute (1..120+). 0 means pre-match. */
  minute: number;
  /** Stoppage minutes added on top of regulation half end (45 / 90). */
  stoppage: number;
  phase: MatchPhase;
  /** Display string e.g. "67:24", "90+3", "ET 103", "PEN", "HT", "FT". */
  display: string;
  /** 0..100 progress within regulation 90 minutes. */
  progressPct: number;
  /** True for last 10' of either half, stoppage time, ET or penalties. */
  isCritical: boolean;
  /** Human "≈ N min remaining" message in active language. */
  remainingLabel: string;
  /** Localized phase label. */
  phaseLabel: string;
  /** True when in stoppage time. */
  inStoppage: boolean;
}

/**
 * Derive phase + clock state. The simulation engine caps the minute at 90 and
 * uses status "HT" / "FT", but we still honor synthetic values >90 so the
 * component can be reused for Extra Time / Penalties screens.
 */
export function deriveMatchClock(
  minute: number,
  status: MatchSnapshot["status"] | "PRE" | "ET" | "PEN" | "SUSP" = "LIVE",
  opts?: { secondOfMinute?: number },
): MatchClockState {
  const lang = typeof window !== "undefined" ? getStoredLang() : "EN";
  const sec = Math.max(0, Math.min(59, Math.floor(opts?.secondOfMinute ?? 0)));

  let phase: MatchPhase;
  if (status === "HT") phase = "halftime";
  else if (status === "FT") phase = "full_time";
  else if (status === "ET") phase = "extra_time";
  else if (status === "PEN") phase = "penalties";
  else if (status === "SUSP") phase = "suspended";
  else if (status === "PRE" || minute <= 0) phase = "pre_match";
  else if (minute <= 45) phase = "first_half";
  else if (minute <= 90) phase = "second_half";
  else if (minute <= 120) phase = "extra_time";
  else phase = "penalties";

  const stoppage =
    phase === "first_half" && minute > 45
      ? minute - 45
      : phase === "second_half" && minute > 90
      ? minute - 90
      : 0;

  const inStoppage = stoppage > 0;

  let display: string;
  switch (phase) {
    case "pre_match":
      display = translate("clock_kickoff_short", lang);
      break;
    case "halftime":
      display = "HT";
      break;
    case "full_time":
      display = "FT";
      break;
    case "suspended":
      display = translate("clock_suspended_short", lang);
      break;
    case "penalties":
      display = "PEN";
      break;
    case "extra_time":
      display = `ET ${minute}:${String(sec).padStart(2, "0")}`;
      break;
    case "first_half":
      display = inStoppage
        ? `45+${stoppage}`
        : `${minute}:${String(sec).padStart(2, "0")}`;
      break;
    case "second_half":
      display = inStoppage
        ? `90+${stoppage}`
        : `${minute}:${String(sec).padStart(2, "0")}`;
      break;
  }

  // Progress vs regulation 90.
  const cappedRegulation = Math.min(90, Math.max(0, minute));
  const progressPct =
    phase === "pre_match"
      ? 0
      : phase === "halftime"
      ? 50
      : phase === "full_time" || phase === "extra_time" || phase === "penalties"
      ? 100
      : Math.round((cappedRegulation / 90) * 100);

  // Critical: last 10' either half, stoppage, ET or PEN.
  const isCritical =
    inStoppage ||
    phase === "extra_time" ||
    phase === "penalties" ||
    (phase === "first_half" && minute >= 35) ||
    (phase === "second_half" && minute >= 80);

  // Remaining label.
  let remainingLabel: string;
  if (phase === "pre_match") remainingLabel = translate("clock_remaining_pre", lang);
  else if (phase === "halftime") remainingLabel = translate("clock_remaining_ht", lang);
  else if (phase === "full_time") remainingLabel = translate("clock_remaining_ft", lang);
  else if (phase === "penalties") remainingLabel = translate("clock_remaining_pen", lang);
  else if (phase === "extra_time")
    remainingLabel = translate("clock_remaining_et", lang);
  else if (phase === "suspended")
    remainingLabel = translate("clock_remaining_suspended", lang);
  else if (phase === "first_half") {
    if (inStoppage) remainingLabel = translate("clock_remaining_stoppage_short", lang);
    else if (minute >= 43) remainingLabel = translate("clock_remaining_half_end_soon", lang);
    else
      remainingLabel = translate("clock_remaining_first_half", lang, {
        n: Math.max(1, 45 - minute),
      });
  } else {
    // second_half
    if (inStoppage) remainingLabel = translate("clock_remaining_stoppage_short", lang);
    else if (minute >= 88) remainingLabel = translate("clock_remaining_match_end_soon", lang);
    else
      remainingLabel = translate("clock_remaining_second_half", lang, {
        n: Math.max(1, 90 - minute),
      });
  }

  const phaseLabel = translate(`clock_phase_${phase}`, lang);

  return {
    minute,
    stoppage,
    phase,
    display,
    progressPct,
    isCritical,
    remainingLabel,
    phaseLabel,
    inStoppage,
  };
}

/** Build a compact temporal context line that the AI Oracle can consume. */
export function buildAiTemporalContext(state: MatchClockState): string {
  return [
    `currentMinute=${state.minute}`,
    `matchPhase=${state.phase}`,
    `stoppage=${state.stoppage}`,
    `estimatedTimeRemaining=${state.remainingLabel}`,
    `isCriticalMoment=${state.isCritical}`,
  ].join(" · ");
}

// ------------------------ Settings (admin-controlled) ------------------------

export interface MatchClockSettings {
  enabled: boolean;
  showRemaining: boolean;
  showProgressBar: boolean;
  highlightCritical: boolean;
}

const SETTINGS_KEY = "ss2026:match-clock-settings";
const DEFAULTS: MatchClockSettings = {
  enabled: true,
  showRemaining: true,
  showProgressBar: true,
  highlightCritical: true,
};

export function getMatchClockSettings(): MatchClockSettings {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<MatchClockSettings>) };
  } catch {
    return DEFAULTS;
  }
}

export function setMatchClockSettings(s: MatchClockSettings) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  window.dispatchEvent(new CustomEvent("ss2026:match-clock-settings", { detail: s }));
}